<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { CenteredContactProps } from "./CenteredContactProps";
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import FormField from "../components/form-field/FormField.vue";
import SubmitButton from "../components/submit-button/SubmitButton.vue";
import PrivacyPolicy from "../components/privacy-policy/PrivacyPolicy.vue";
import { useContactForm } from "../common/useContactForm";

const props = defineProps<{
    block: PageBlock<CenteredContactProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const { agreed, formSubmitted, isSubmitting, showSuccessMessage, showErrorMessage, handleSubmit } =
    useContactForm({
        hookUrl: dataValues.value.submitButton.data.hookUrl,
        privacyPolicyHidden: dataValues.value.privacyPolicy.isHidden,
        validationMessage: dataValues.value.privacyPolicy.data.validationMessage,
        successMessage: dataValues.value.submitButton.data.successMessage,
        errorMessage: dataValues.value.submitButton.data.errorMessage,
    });
</script>

<template>
    <Section :id="block.webId" class="relative isolate" :backgroundColor="styleValues.background">
        <FullSpacing>
            <div class="mx-auto max-w-2xl text-center">
                <EditableElement
                    v-if="!dataValues.title.isHidden"
                    :blockId="block.id"
                    field-name="title"
                >
                    <Heading
                        v-bind="dataValues.title.data"
                        :textColor="styleValues.text"
                        class="text-4xl sm:text-5xl"
                    />
                </EditableElement>
                <EditableElement
                    v-if="!dataValues.description.isHidden"
                    :blockId="block.id"
                    field-name="description"
                >
                    <Paragraph
                        v-bind="dataValues.description.data"
                        :textColor="styleValues.text"
                        class="mt-6 text-lg/8 opacity-70"
                    />
                </EditableElement>
            </div>
            <form class="mx-auto mt-16 max-w-xl sm:mt-20" @submit="handleSubmit">
                <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                    <EditableElement
                        v-for="field in dataValues.fields.data.items"
                        :key="field.id"
                        :blockId="block.id"
                        :field-name="`fields_${field.id}`"
                        :hideable="false"
                    >
                        <FormField
                            v-bind="field"
                            :label-color="styleValues.text"
                            :accent-color="styleValues.brand"
                            :border-color="styleValues.border"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.privacyPolicy.isHidden"
                        :blockId="block.id"
                        field-name="privacyPolicy"
                    >
                        <PrivacyPolicy
                            v-bind="dataValues.privacyPolicy.data"
                            :accent-color="styleValues.brand"
                            v-model:agreed="agreed"
                        />
                    </EditableElement>
                </div>
                <div class="mt-10 space-y-4">
                    <EditableElement
                        v-if="!dataValues.submitButton.isHidden"
                        :blockId="block.id"
                        field-name="submitButton"
                    >
                        <SubmitButton
                            :label="dataValues.submitButton.data.label"
                            :text-color="styleValues.background"
                            :background-color="styleValues.brand"
                            :is-submitting="isSubmitting"
                        />
                    </EditableElement>
                </div>
                <FormMessages
                    :formSubmitted="formSubmitted"
                    :agreed="agreed"
                    :validationMessage="dataValues.privacyPolicy.data.validationMessage"
                    :successMessage="dataValues.submitButton.data.successMessage"
                    :errorMessage="dataValues.submitButton.data.errorMessage"
                    :showSuccessMessage="showSuccessMessage"
                    :showErrorMessage="showErrorMessage"
                />
            </form>
        </FullSpacing>
    </Section>
</template>
